
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteGroupRepositoryInterface, { WebsiteGroupItem } from '../../../services/WebsiteGroup/interfaces';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { UserBaseInfoInterface, UserInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import UserPicker from '@/components/user/UserPicker.vue';
import WebsitePicker from '@/components/website/WebsitePicker.vue';
import { PlanRepositoryInterface } from '@/services/Plan/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import VComponent from '@/components/base/VComponent.vue';
import { UsageRepositoryInterface } from "@/services/Usage/interfaces";
import { OriginVhostInterface } from "@/services/Vhost/Origin/Interfaces";

@Component({
  components: {
    VComponent,
    WebsitePicker,
    UserPicker,
  },
  mixins: [Themeable],
  inject: [
    'websiteGroupRepository',
    'websiteRepository',
    'userRepository',
    'planRepository',
    'serverRepository',
    'usageRepository',
  ],
})
export default class UsageList extends Vue {
  public loading: boolean = true;

  public options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 10,
  };

  public totalGroupsCount = 0;
  public ownerFilter: string | null = null;

  public domainFilter = '';

  private websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private planRepository!: PlanRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private usageRepository!: UsageRepositoryInterface;

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Plan', value: 'plan' },
    { text: 'Main Website', value: 'mainWebsite' },
    { text: 'Server', value: 'server' },
    { text: 'Usage', value: 'usage' },
    { text: 'Actions', value: 'actions' },
  ];

  public groups: WebsiteGroupItem[] = [];
  public websites: Website[] = [];
  public users: UserBaseInfoInterface[] = [];
  public plansMap: { [key: number]: string } = {};
  public usageUsedMap: { [key: number]: number } = {};
  public usageQuotaMap: { [key: number]: number } = {};

  public async created(): Promise<void> {
    await this.loadPlans();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;

      await this.loadWebsiteGroups();

      await Promise.all([
        this.loadWebsites(),
        this.loadServers(),
        this.loadUsage()
      ]);

      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(`An issue ocurred while retrieving website groups. ${err}`);
    }
  }

  public async loadWebsiteGroups(page?: number, clearPage?: boolean): Promise<void> {
    try {
      this.loading = true;
      if (clearPage) {
        this.options.page = 1;
      }

      const groupsData = await this.websiteGroupRepository.searchGroups(undefined, this.ownerFilter, this.options.page, this.domainFilter);

      if (!groupsData?.groups) {
        this.groups = [];
        this.totalGroupsCount = 0;
        this.loading = false;

        return;
      }

      this.groups = groupsData.groups;
      this.totalGroupsCount = groupsData.count;

      await Promise.all([
        this.loadWebsites(),
        this.loadServers(),
        this.loadUsage()
      ]);

      this.loading = false;
    } catch (err) {
      this.groups = [];
      this.totalGroupsCount = 0;
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  private async loadWebsites(): Promise<void> {
    const ids = this.groups.map((group) => group.mainWebsite);
    if (!ids.length) {
      return;
    }

    this.websites = await this.websiteRepository.getWebsitesByIds(ids);
  }

  public async loadPlans(): Promise<void> {
    const plans = await this.planRepository.getAll();
    plans.forEach((plan) => {
      this.plansMap[plan.id] = plan.name;
    });
  }

  public async loadServers(): Promise<void> {
    const serversIds: string[] = [];
    this.websites.forEach((website) => {
      if (!serversIds.includes(website.originServer)) {
        serversIds.push(website.originServer);
      }
    });

    if (!serversIds.length) {
      return;
    }

    const servers = await this.serverRepository.getServersByIds(serversIds);
    const serversMap: { [key: string]: string } = {};
    servers.forEach((server) => {
      serversMap[server._id] = server.hostname;
    });


    this.websites.forEach((website) => {
      const groupIndex = this.groups.findIndex((g) => g.mainWebsite === website.id);
      if (groupIndex === -1) {
        return;
      }

      this.$set(this.groups[groupIndex], 'server', serversMap[website.originServer]);
    });
  }

  public async loadUsage(): Promise<void> {
    await Promise.all(this.groups.map(x => this.usageRepository.getDiskUsageByGroup(x.id).then(y => {
      this.usageUsedMap[x.id] = y.quotaSnapshot.blockUsed;
      this.usageQuotaMap[x.id] = y.quotaSnapshot.blockHard;
    }).catch(() => {})));
  }

  public onDomainFilterChange(): void {
      this.loadWebsiteGroups(undefined, true);
  }

  public getUsedBytes(groupId: number): number {
    return (this.usageUsedMap[groupId] ?? 0) / 1024;
  }

  public getQuotaBytes(groupId: number): number {
    return (this.usageQuotaMap[groupId] ?? 0) / 1024;
  }

  private viewUsage(groupId: number): void {
    this.$router.push({ name: 'usage_details', params: { groupId } });
  }

  public formatBytes(kib: number | null | undefined): string {
    if (kib === null || kib === undefined) {
      return '0';
    }

    // KiB to MB
    const mb = kib / 976.6;

    // If less than 1 GB -show in MB
    if (mb < 1000) {
      return `${Math.round(mb)} MB`;
    }

    // 1 GB or more - show in GB with 2 decimal
    const gb = mb / 1000;
    return `${gb.toFixed(2)} GB`;
  }

  private getWebsiteDomain(websiteId: number): string {
    const website = this.websites.find((w) => w.id === websiteId);
    if (!website) {
      return '';
    }

    return website.domain;
  }

  private getUserEmail(userId: string): string {
    const user = this.users.find((w) => w.id === userId);
    if (!user) {
      return '';
    }

    return user.email;
  }
}
